import { useAuth, useUser } from "@clerk/clerk-react";
import { useState } from "react";

const PLAN_NAMES: Record<string, string> = {
  "price_1TazAEAnI7zzun0R5wGrD2ol": "Anual",
  "price_1Taz9eAnI7zzun0RCHhB5NOg": "Trimestral",
  "price_1Taz9MAnI7zzun0R92AYw0ld": "Mensal",
  "price_1Tct02AnI7zzun0RWyPay7gK": "Vitalício"
};

// Wrapper preview-safe: em ?preview=app (e demais previews DEV) o App é renderizado
// SEM ClerkProvider, então os hooks useAuth/useUser de SubscriptionBarLive quebrariam
// ("useAuth can only be used within <ClerkProvider>"). Nesse modo a barra não aparece
// (não há sessão a exibir/gerenciar). Fora de preview, comportamento inalterado.
export function SubscriptionBar() {
  const isPreview =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("preview") != null;
  if (isPreview) return null;
  return <SubscriptionBarLive />;
}

function SubscriptionBarLive() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const [busy, setBusy] = useState(false);

  const meta = user?.publicMetadata as Record<string, unknown> | undefined;
  const isFounder = meta?.founderAccess === true;
  const stripeCustomerId = typeof meta?.stripeCustomerId === "string" ? meta.stripeCustomerId : null;
  const stripePriceId = typeof meta?.stripePriceId === "string" ? meta.stripePriceId : null;
  const subscriptionStatus = typeof meta?.subscriptionStatus === "string" ? meta.subscriptionStatus : null;
  const hasSubscription = Boolean(stripeCustomerId);
  // Fallback: usuários com subscriptionStatus "active" sem stripeCustomerId
  // (pagamentos antigos one-time ou acesso manual)
  const isActiveSubscriber = subscriptionStatus === "active" || subscriptionStatus === "trialing";

  const displayName =
    user?.fullName?.trim() ||
    user?.username ||
    user?.primaryEmailAddress?.emailAddress?.split("@")[0] ||
    "Usuário";

  const planName = isFounder
    ? "Vitalício"
    : stripePriceId
      ? (PLAN_NAMES[stripePriceId] ?? "Assinante")
      : (hasSubscription || isActiveSubscriber)
        ? "Assinante"
        : null;

  const openPortal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const token = await getToken({ skipCache: true });
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      const res = await fetch("/api/create-portal-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ returnUrl })
      });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) throw new Error(data.error ?? "Erro ao abrir portal.");
      window.location.assign(data.url);
    } catch (err) {
      setBusy(false);
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      alert(`Não foi possível abrir o portal de assinatura.\n\n${msg}`);
    }
  };

  if (!planName) return null;

  // Pode abrir o portal apenas se tiver stripeCustomerId vinculado
  const canManage = hasSubscription && !isFounder;

  return (
    <div className="sub-bar">
      <div className="sub-bar__user">
        <span className="sub-bar__name">{displayName}</span>
        <span className="sub-bar__plan">{planName}</span>
      </div>
      <button
        className="sub-bar__manage"
        type="button"
        onClick={canManage ? () => { void openPortal(); } : undefined}
        disabled={!canManage || busy}
        title={
          isFounder
            ? "Acesso vitalício — sem renovação necessária"
            : canManage
              ? "Alterar plano, cartão ou cancelar"
              : "Acesso ativo"
        }
      >
        {isFounder
          ? "Acesso vitalício ✓"
          : canManage
            ? busy ? "Abrindo..." : "Gerencie sua assinatura →"
            : "Acesso ativo ✓"}
      </button>
    </div>
  );
}
